import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Alert, 
  Typography, 
  Modal,
  Space,
  message
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  ExclamationCircleOutlined,
  KeyOutlined,
  MailOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../layouts/AuthLayout';
import logo from '../../assets/logo.png';
import { useAuth } from '../../hooks/useAuth';
import { sendTemporaryPassword } from '../../services/users'; // Importa el servicio

const { Text } = Typography;

export default function LoginPage() {
    const [loading, setLoading] = useState(false);
    const [globalError, setGlobalError] = useState(null);
    const [passwordResetModal, setPasswordResetModal] = useState(false);
    const [forgotPasswordModal, setForgotPasswordModal] = useState(false); // Nuevo estado
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false); // Nuevo estado
    const [forgotPasswordError, setForgotPasswordError] = useState(null); // Nuevo estado
    const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState(null);
    const [form] = Form.useForm();
    const [resetForm] = Form.useForm();
    const [forgotForm] = Form.useForm(); // Nuevo form
    const navigate = useNavigate();
    
    const { 
      login, 
      isAuthenticated, 
      loading: authLoading,
      needsPasswordReset,
      tempUser,
      completePasswordReset,
      clearPasswordReset
    } = useAuth();

    const mockEnabled = useMemo(
        () => (process.env.REACT_APP_ENABLE_MOCK_AUTH ?? 'true') !== 'false',
        []
    );

    const handleInputChange = () => {
        if (globalError) {
            setGlobalError(null);
        }
    };

    useEffect(() => {
        if (!authLoading && isAuthenticated && !needsPasswordReset) {
            navigate('/', { replace: true });
        }
    }, [authLoading, isAuthenticated, needsPasswordReset, navigate]);

    useEffect(() => {
        if (needsPasswordReset && tempUser) {
            setPasswordResetModal(true);
        }
    }, [needsPasswordReset, tempUser]);

    const onFinish = async (values) => {
        setLoading(true);
        setGlobalError(null);
        
        try {
            const result = await login({
                email: values.email,
                password: values.password,
            });
            
            if (result.needsPasswordReset) {
                return;
            }
            
            navigate('/');
        } catch (err) {
            console.error('Login error:', err); 
            
            const status = err?.response?.status;
            const data = err?.response?.data;
            const errorCode = data?.code || err?.code;
            const errorMessage = data?.message || err?.message || 'Error desconocido';

            form.setFields([]);

            if (errorCode === 'INVALID_CREDENTIALS') {
                setGlobalError('Email o contraseña incorrectos. Verifica tus credenciales e inténtalo de nuevo.');
            } else if (status === 400 || status === 401 || status === 422) {
                const fieldErrors = data?.fieldErrors || {};
                const fields = Object.entries(fieldErrors).map(([name, errors]) => ({
                    name,
                    errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
                }));

                if (fields.length) {
                    form.setFields(fields);
                } else {
                    setGlobalError(errorMessage === 'Unknown error' ? 'Credenciales incorrectas. Verifica tu email y contraseña.' : errorMessage);
                }
            } else if (status === 403) {
                setGlobalError('Tu cuenta no tiene permisos para acceder. Contacta al administrador.');
            } else if (status === 429) {
                setGlobalError('Demasiados intentos de inicio de sesión. Espera unos minutos antes de intentar de nuevo.');
            } else if (status >= 500) {
                setGlobalError('Error del servidor. Por favor, inténtalo más tarde.');
            } else if (err.code === 'NETWORK_ERROR' || err.code === 'VALIDATION_ERROR' || !navigator.onLine) {
                setGlobalError('Sin conexión a internet. Verifica tu conexión e inténtalo de nuevo.');
            } else {
                const friendlyMessage = errorMessage === 'Unknown error' 
                    ? 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo.' 
                    : errorMessage;
                setGlobalError(friendlyMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // NUEVA FUNCIÓN: Manejar envío de contraseña temporal
    const handleForgotPassword = async (values) => {
        setForgotPasswordLoading(true);
        setForgotPasswordError(null);
        
        try {

            let valuesArray = {
                email: values.email
            }
            const response = await sendTemporaryPassword(valuesArray);
            
            // Mostrar mensaje de éxito
            message.success({
                content: 'Se ha enviado una contraseña temporal a tu correo electrónico',
                duration: 5,
            });
            
            // Cerrar modal y resetear form
            setForgotPasswordModal(false);
            forgotForm.resetFields();
            
        } catch (error) {
            console.error('Forgot password error:', error);
            
            const errorData = error?.response?.data;
            let errorMessage = 'Error al enviar contraseña temporal';
            
            if (errorData?.message) {
                errorMessage = errorData.message;
            } else if (errorData?.error?.message) {
                errorMessage = errorData.error.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setForgotPasswordError(errorMessage);
            
            // Mostrar errores de validación
            if (errorData?.fieldErrors) {
                const fields = Object.entries(errorData.fieldErrors).map(([name, errors]) => ({
                    name,
                    errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
                }));
                
                if (fields.length) {
                    forgotForm.setFields(fields);
                }
            }
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    const handlePasswordReset = async (values) => {
        if (!tempUser) return;
        
        setResetPasswordLoading(true);
        setResetPasswordError(null);
        
        try {
            const resetData = {
                userId: tempUser.id || tempUser._id,
                newPassword: values.newPassword,
                confirmNewPassword: values.confirmNewPassword
            };
            
            await completePasswordReset(resetData);
            
            setPasswordResetModal(false);
            resetForm.resetFields();
            
            navigate('/', { replace: true });
            
        } catch (error) {
            console.error('Password reset error:', error);
            
            const errorData = error?.response?.data;
            let errorMessage = 'Error al cambiar la contraseña';
            
            if (errorData?.message) {
                errorMessage = errorData.message;
            } else if (errorData?.error?.message) {
                errorMessage = errorData.error.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setResetPasswordError(errorMessage);
            
            if (errorData?.fieldErrors) {
                const fields = Object.entries(errorData.fieldErrors).map(([name, errors]) => ({
                    name,
                    errors: Array.isArray(errors) ? errors : [String(errors || 'Error')],
                }));
                
                if (fields.length) {
                    resetForm.setFields(fields);
                }
            }
        } finally {
            setResetPasswordLoading(false);
        }
    };

    const handleCancelReset = () => {
        clearPasswordReset();
        setPasswordResetModal(false);
        resetForm.resetFields();
        window.location.reload();
    };

    // NUEVA FUNCIÓN: Cancelar modal de olvidó contraseña
    const handleCancelForgotPassword = () => {
        setForgotPasswordModal(false);
        forgotForm.resetFields();
        setForgotPasswordError(null);
    };

    const cardStyle = {
        width: '100%',
        maxWidth: 480,
        borderRadius: 16,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    };

    const logoStyle = {
        display: 'block',
        margin: '0 auto 8px',
        width: 160,
        height: 'auto',
        objectFit: 'contain',
    };

    return (
        <>
            <AuthLayout>
                <Card style={cardStyle} bodyStyle={{ padding: 24 }}>
                    <img src={logo} alt="DOCXI" style={logoStyle} />

                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <Text type="secondary">Bienvenido de vuelta</Text>
                    </div>

                    {mockEnabled && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Modo MOCK activado"
                            description="Usa cualquier email y contraseña."
                        />
                    )}

                    {globalError && (
                        <Alert
                            type="error"
                            showIcon
                            icon={<ExclamationCircleOutlined />}
                            style={{ marginBottom: 16 }}
                            message="Error de inicio de sesión"
                            description={globalError}
                            closable
                            onClose={() => setGlobalError(null)}
                        />
                    )}

                    <Form
                        form={form}
                        name="login"
                        layout="vertical"
                        requiredMark={false}
                        onFinish={onFinish}
                        validateTrigger={['onBlur', 'onSubmit']}
                        initialValues={{ remember: true }}
                    >
                        <Form.Item
                            label="Email"
                            name="email"
                            rules={[
                                { required: true, message: 'El email es obligatorio.' },
                                { type: 'email', message: 'Formato de email inválido.' },
                                { min: 5, message: 'El email debe tener al menos 5 caracteres.' },
                                { max: 100, message: 'El email no puede exceder 100 caracteres.' },
                            ]}
                        >
                            <Input
                                size="large"
                                prefix={<UserOutlined aria-hidden="true" />}
                                placeholder="tu@email.com"
                                aria-label="Correo electrónico"
                                autoComplete="email"
                                onChange={handleInputChange}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Contraseña"
                            name="password"
                            rules={[
                                { required: true, message: 'La contraseña es obligatoria.' },
                                { min: 6, message: 'La contraseña debe tener al menos 6 caracteres.' },
                                { max: 50, message: 'La contraseña no puede exceder 50 caracteres.' },
                            ]}
                        >
                            <Input.Password
                                size="large"
                                prefix={<LockOutlined aria-hidden="true" />}
                                placeholder="Tu contraseña"
                                aria-label="Contraseña"
                                autoComplete="current-password"
                                onChange={handleInputChange}
                            />
                        </Form.Item>

                        <Form.Item style={{ marginTop: 8 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                block
                                loading={loading}
                                aria-label="Iniciar sesión"
                            >
                                Iniciar Sesión
                            </Button>
                        </Form.Item>

                        {/* MODIFICADO: Enlace funcional */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                            <Button 
                                type="link" 
                                onClick={() => setForgotPasswordModal(true)}
                                aria-label="¿Olvidaste tu contraseña?"
                            >
                                ¿Olvidaste tu contraseña?
                            </Button>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 16, fontSize: 12 }}>
                            <a href="https://docxidev.netlify.app/terminos">
                                Términos y Condiciones
                            </a>
                        </div>
                    </Form>                
                </Card>
            </AuthLayout>

            {/* Modal para cambio de contraseña forzado */}
            <Modal
                title={
                    <Space>
                        <KeyOutlined style={{ color: '#1890ff' }} />
                        <span>Cambio de contraseña requerido</span>
                    </Space>
                }
                open={passwordResetModal}
                onCancel={handleCancelReset}
                footer={null}
                closable={false}
                maskClosable={false}
                keyboard={false}
                width={500}
                centered
                destroyOnClose
            >
                <div style={{ marginBottom: 20 }}>
                    <Alert
                        type="warning"
                        message="¡Atención! Se requiere cambio de contraseña"
                        description={
                            <div>
                                <p>Por motivos de seguridad, es obligatorio que actualices tu contraseña para continuar.</p>
                                {tempUser?.email && (
                                    <p style={{ marginTop: 8 }}>
                                        <strong>Cuenta:</strong> {tempUser.email}
                                    </p>
                                )}
                            </div>
                        }
                        showIcon
                    />
                </div>

                {resetPasswordError && (
                    <Alert
                        type="error"
                        message="Error al cambiar contraseña"
                        description={resetPasswordError}
                        style={{ marginBottom: 16 }}
                        closable
                        onClose={() => setResetPasswordError(null)}
                    />
                )}

                <Form
                    form={resetForm}
                    layout="vertical"
                    onFinish={handlePasswordReset}
                    requiredMark={false}
                    validateTrigger={['onBlur', 'onChange']}
                >
                    <Form.Item
                        label="Nueva contraseña"
                        name="newPassword"
                        rules={[
                            { required: true, message: 'La nueva contraseña es obligatoria' },
                            { min: 8, message: 'Mínimo 8 caracteres' },
                            { 
                                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{8,}$/,
                                message: 'Debe incluir mayúscula, minúscula, número y carácter especial'
                            }
                        ]}
                        help="Mínimo 8 caracteres con mayúscula, minúscula, número y carácter especial"
                    >
                        <Input.Password
                            size="large"
                            placeholder="Ingresa tu nueva contraseña"
                            autoFocus
                        />
                    </Form.Item>

                    <Form.Item
                        label="Confirmar nueva contraseña"
                        name="confirmNewPassword"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Confirma tu nueva contraseña' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Las contraseñas no coinciden'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            size="large"
                            placeholder="Confirma tu nueva contraseña"
                        />
                    </Form.Item>

                    <div style={{ marginTop: 24, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCancelReset} disabled={resetPasswordLoading}>
                                Cancelar
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                loading={resetPasswordLoading}
                                icon={<KeyOutlined />}
                            >
                                Cambiar contraseña
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            {/* NUEVO MODAL: Olvidé mi contraseña */}
            <Modal
                title={
                    <Space>
                        <MailOutlined style={{ color: '#1890ff' }} />
                        <span>Recuperar contraseña</span>
                    </Space>
                }
                open={forgotPasswordModal}
                onCancel={handleCancelForgotPassword}
                footer={null}
                width={400}
                centered
                destroyOnClose
            >
                <div style={{ marginBottom: 20 }}>
                    <Alert
                        type="info"
                        message="Recuperación de contraseña"
                        description="Ingresa tu correo electrónico y te enviaremos una contraseña temporal para que puedas acceder a tu cuenta."
                        showIcon
                    />
                </div>

                {forgotPasswordError && (
                    <Alert
                        type="error"
                        message="Error al enviar contraseña temporal"
                        description={forgotPasswordError}
                        style={{ marginBottom: 16 }}
                        closable
                        onClose={() => setForgotPasswordError(null)}
                    />
                )}

                <Form
                    form={forgotForm}
                    layout="vertical"
                    onFinish={handleForgotPassword}
                    requiredMark={false}
                    validateTrigger={['onBlur', 'onChange']}
                >
                    <Form.Item
                        label="Correo electrónico"
                        name="email"
                        rules={[
                            { required: true, message: 'El correo electrónico es obligatorio' },
                            { type: 'email', message: 'Ingresa un correo electrónico válido' }
                        ]}
                    >
                        <Input
                            size="large"
                            prefix={<MailOutlined />}
                            placeholder="tu@email.com"
                            autoFocus
                        />
                    </Form.Item>

                    <div style={{ marginTop: 24, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={handleCancelForgotPassword} disabled={forgotPasswordLoading}>
                                Cancelar
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                loading={forgotPasswordLoading}
                                icon={<MailOutlined />}
                            >
                                Enviar contraseña temporal
                            </Button>
                        </Space>
                    </div>
                </Form>

                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    <Alert
                        type="warning"
                        message="Importante"
                        description={
                            <div style={{ fontSize: 12 }}>
                                <p>• Revisa tu bandeja de entrada y carpeta de spam</p>
                                <p>• La contraseña temporal expira en 24 horas</p>
                                <p>• Después de iniciar sesión, cambia tu contraseña inmediatamente</p>
                            </div>
                        }
                        showIcon={false}
                    />
                </div>
            </Modal>
        </>
    );
}